import type { VersionOut } from "../api";
import type { Address, Digest, ModuleRecord, SourceState, SplitShare, VersionRef } from "./types";

/** Display names for the reviewed modules. Protocol identifiers are never renamed; these are labels only. */
export const DISPLAY_NAMES: Record<string, string> = {
  "ascii-lower": "lowercase text",
  "decimal-scale": "scale decimals",
  "json-drop-fields": "remove selected fields",
  "json-sort-keys": "sort JSON keys",
  "line-endings": "normalize line endings",
  "trim-lines": "trim lines",
};

/** Human task name for a module id; unknown modules fall back to the identifier with dashes as spaces. */
export function displayName(moduleId: string): string {
  return DISPLAY_NAMES[moduleId] ?? moduleId.replace(/[-_]+/g, " ");
}

/** Sentence-case version for headings: "Remove selected fields". */
export function displayTitle(moduleId: string): string {
  const n = displayName(moduleId);
  return n.charAt(0).toUpperCase() + n.slice(1);
}

export function asDigest(d: string): Digest {
  const hex = d.toLowerCase().replace(/^sha256:/, "").replace(/^0x/, "");
  return `sha256:${hex}`;
}

export function toVersionRef(v: Pick<VersionOut, "module" | "digest" | "artifact_digest" | "version">): VersionRef {
  return { moduleId: v.module, manifestDigest: asDigest(v.digest), artifactDigest: asDigest(v.artifact_digest), version: v.version };
}

function obj(x: unknown): Record<string, unknown> {
  return x && typeof x === "object" ? (x as Record<string, unknown>) : {};
}
const str = (x: unknown): string | null => (typeof x === "string" ? x : null);
const num = (x: unknown): number | null => (typeof x === "number" && Number.isFinite(x) ? x : null);

export function sourceState(v: Pick<VersionOut, "source" | "source_status">): SourceState {
  const s = str(obj(v.source).status) ?? v.source_status;
  return s === "available" || s === "unavailable" || s === "verified" ? s : "unknown";
}

/** Plain explanation of a source state. Available source is not an independently verified build. */
export function sourceLabel(s: SourceState): string {
  switch (s) {
    case "verified": return "Source available, build independently verified";
    case "available": return "Source available, build not independently verified";
    case "unavailable": return "Source not published";
    default: return "Source status unknown";
  }
}

export function normalizeModule(v: VersionOut): ModuleRecord {
  const input = obj(v.input), output = obj(v.output), limits = obj(v.limits), pub = v.publisher;
  const splits: SplitShare[] = Array.isArray(v.splits)
    ? (v.splits as unknown[]).map((s) => ({ address: String(obj(s).address) as Address, bps: Number(obj(s).bps) }))
    : [];
  const maxChildCalls = num(limits.max_child_calls) ?? 0;
  return {
    ref: toVersionRef(v),
    moduleId: v.module,
    displayName: displayName(v.module),
    purpose: v.summary,
    category: v.category,
    inputType: str(input.content_type) ?? "application/octet-stream",
    outputType: str(output.content_type) ?? "application/octet-stream",
    maxInputBytes: num(input.max_bytes),
    priceMicro: v.price_micro,
    publisher: {
      handle: pub?.handle ?? null,
      displayName: pub?.display_name ?? null,
      address: (pub?.address as Address | undefined) ?? null,
      operator: Boolean(pub?.operator),
    },
    splits,
    source: sourceState(v),
    sourceNote: str(obj(v.source).note),
    limits: { fuel: num(limits.fuel), memoryBytes: num(limits.memory_bytes), maxChildCalls },
    maxInvocations: 1 + maxChildCalls,
    hasParams: Boolean(v.params_schema && Object.keys(obj(v.params_schema)).length),
    publishedAt: v.published_at,
    status: v.status,
  };
}

/** Short type label for ports: "JSON", "text", "bytes". */
export function typeLabel(contentType: string): string {
  if (contentType === "application/json") return "JSON";
  if (contentType.startsWith("text/")) return "text";
  if (contentType === "application/octet-stream") return "bytes";
  return contentType;
}

export const moduleHref = (r: Pick<VersionRef, "moduleId" | "manifestDigest">) =>
  `/modules/${encodeURIComponent(r.moduleId)}/versions/${encodeURIComponent(r.manifestDigest)}`;
